import Drawer from '@material-ui/core/Drawer'
import IconButton from '@material-ui/core/IconButton'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import RemoveIcon from '@material-ui/icons/Remove'
import { Link } from 'gatsby'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import chirpyestTextLogo from '../../assets/images/chirpyest-text-logo.svg'
import { ROUTES } from '../../constants'
import { NavItem } from './navConfig'
import { styles } from './styles'
import Anchor from '../anchor'

interface MobileMenuProps {
  id: string
  open: boolean
  isLoggedIn: boolean
  navItems: NavItem[]
  onClose: () => void
  onSignIn: () => void
  onSignOut: () => void
  onReferFriend?: () => void
  isReferralBonus?: boolean
}

type Classes = ReturnType<typeof styles>

const renderReferFriendItem = (
  item: NavItem,
  classes: Classes,
  onReferFriend?: () => void,
  isReferralBonus?: boolean,
  isCopied?: boolean
) => {
  const label = isReferralBonus
    ? item.label
    : item.label.replace('- earn $10!', '')
  return (
    <div key={item.key} className={classes.mobileNavSubLink}>
      {label}{' '}
      <button
        type="button"
        className={classes.mobileNavCopyLink}
        onClick={() => onReferFriend?.()}
      >
        {isCopied ? 'copied' : 'copy link'}
      </button>
    </div>
  )
}

const renderLeafItem = (
  item: NavItem,
  classes: Classes,
  onSelect: () => void,
  onReferFriend?: () => void,
  className: string = classes.mobileNavLink,
  isReferralBonus?: boolean,
  isCopied?: boolean
) => {
  if (item.action === 'referFriend') {
    return renderReferFriendItem(
      item,
      classes,
      onReferFriend,
      isReferralBonus,
      isCopied
    )
  }
  if (item.external && item.to) {
    return (
      <Anchor
        key={item.key}
        href={item.to}
        className={className}
        onClick={onSelect}
      >
        {item.label}
      </Anchor>
    )
  }
  return (
    <Link
      key={item.key}
      to={item.to || '/'}
      className={className}
      onClick={onSelect}
    >
      {item.label}
    </Link>
  )
}

interface NavGroupProps {
  item: NavItem
  classes: Classes
  onSelect: () => void
  onReferFriend?: () => void
  isReferralBonus?: boolean
  isCopied?: boolean
}

const ExpandableItem = ({
  item,
  classes,
  onSelect,
  onReferFriend,
  isReferralBonus,
  isCopied,
}: NavGroupProps) => {
  const [open, setOpen] = useState(false)
  const panelId = `mobile-nav-${item.key}`
  return (
    <div>
      <button
        type="button"
        className={classes.mobileNavToggle}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(o => !o)}
      >
        <span>{item.label}</span>
        {open ? (
          <RemoveIcon className={classes.mobileNavToggleIcon} />
        ) : (
          <AddIcon className={classes.mobileNavToggleIcon} />
        )}
      </button>
      {open && (
        <div id={panelId} className={classes.mobileNavChildren}>
          {item.children!.map(child =>
            renderLeafItem(
              child,
              classes,
              onSelect,
              onReferFriend,
              classes.mobileNavSubLink,
              isReferralBonus,
              isCopied
            )
          )}
        </div>
      )}
    </div>
  )
}

const StaticSection = ({
  item,
  classes,
  onSelect,
  onReferFriend,
  isReferralBonus,
  isCopied,
}: NavGroupProps) => (
  <div>
    <div className={classes.mobileNavSectionLabel}>{item.label}</div>
    <div className={classes.mobileNavChildren}>
      {item.children!.map(child =>
        renderLeafItem(
          child,
          classes,
          onSelect,
          onReferFriend,
          classes.mobileNavSubLink,
          isReferralBonus,
          isCopied
        )
      )}
    </div>
  </div>
)

const renderItem = (
  item: NavItem,
  classes: Classes,
  onSelect: () => void,
  onReferFriend?: () => void,
  isReferralBonus?: boolean,
  isCopied?: boolean
) => {
  if (item.children && item.children.length > 0) {
    if (item.collapsible === false) {
      return (
        <StaticSection
          key={item.key}
          item={item}
          classes={classes}
          onSelect={onSelect}
          onReferFriend={onReferFriend}
          isReferralBonus={isReferralBonus}
          isCopied={isCopied}
        />
      )
    }
    return (
      <ExpandableItem
        key={item.key}
        item={item}
        classes={classes}
        onSelect={onSelect}
        onReferFriend={onReferFriend}
        isReferralBonus={isReferralBonus}
        isCopied={isCopied}
      />
    )
  }
  return renderLeafItem(
    item,
    classes,
    onSelect,
    onReferFriend,
    classes.mobileNavLink,
    isReferralBonus,
    isCopied
  )
}

const MobileMenu = ({
  id,
  open,
  isLoggedIn,
  navItems,
  onClose,
  onSignIn,
  onSignOut,
  onReferFriend,
  isReferralBonus,
}: MobileMenuProps) => {
  const { t } = useTranslation()
  const classes = styles()
  const [isCopied, setIsCopied] = useState(false)

  const handleReferFriend = () => {
    onReferFriend?.()
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 2000)
  }

  return (
    <Drawer
      anchor="top"
      open={open}
      onClose={onClose}
      transitionDuration={250}
      classes={{ paper: classes.drawerPaper }}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        id,
        role: 'dialog',
        'aria-modal': true,
        'aria-label': t('header.menu'),
      }}
    >
      <div className={classes.mobileMenu}>
        <div className={classes.mobileMenuHeader}>
          <IconButton
            onClick={onClose}
            aria-label={t('header.closeMenu')}
            className={classes.mobileMenuCloseBtn}
            disableRipple
          >
            <CloseIcon />
          </IconButton>
          <Link
            to={isLoggedIn ? ROUTES.member : ROUTES.home}
            onClick={onClose}
            className={classes.mobileMenuLogoLink}
            aria-label={t('shared.chirpyest')}
          >
            <img
              src={chirpyestTextLogo}
              alt={t('shared.chirpyest')}
              className={classes.mobileMenuLogo}
            />
          </Link>
        </div>

        <nav className={classes.mobileNav} aria-label={t('header.menu')}>
          {navItems.map(item =>
            renderItem(
              item,
              classes,
              onClose,
              handleReferFriend,
              isReferralBonus,
              isCopied
            )
          )}
        </nav>

        <div className={classes.mobileActions}>
          {!isLoggedIn && (
            <>
              <Link
                to={ROUTES.join}
                className={classes.secondaryButton}
                onClick={onClose}
              >
                {t('shared.join')}
              </Link>
              <button
                type="button"
                className={classes.primaryButton}
                onClick={() => {
                  onClose()
                  onSignIn()
                }}
              >
                {t('header.signIn')}
              </button>
            </>
          )}
          {isLoggedIn && (
            <button
              type="button"
              className={classes.mobileSignOutButton}
              onClick={() => {
                onClose()
                onSignOut()
              }}
            >
              {t('header.signOut')}
            </button>
          )}
        </div>
      </div>
    </Drawer>
  )
}

export default MobileMenu
